import React, {useEffect, useMemo, useState} from "react";
import {Col} from "antd";
import moment from "moment";
import {useStore} from "../../../hooks/useStore";
import {useFetch} from "../../../hooks/useFetch";
import {isPoolWorkType, SHIFT_WORK_TYPES} from "../../../constants/shiftWorkTypes";
import {
    DEDICATED_HIDDEN_SHIFT_STATUSES,
    REPORT_ONLY_SHIFT_STATUSES
} from "../../../constants/shiftStatuses";
import {
    CityFilter,
    CompanyFilter,
    DateFilter,
    DateRangeFilter,
    PoolFilter,
    SearchText,
    SubCompanyFilter,
    SubCompanyWorkTypeFilter
} from "../../_defaultComponents/FilterComponents";
import PoolShiftStatusFilter from "../../_defaultComponents/FilterComponents/PoolShiftStatusFilter";

/**
 * Havuz ve dedike vardiyaların ortak filtre barı. `isSnapshot` ile Snapshot sayfası eski
 * filtre setinde kalır (alt firma tipi, havuz bölgesi ve durum filtreleri gizlenir).
 */
const ShiftManagementFilters = ({
    setCurrentPage,
    filters,
    setFilters,
    setWorkType,
    workType,
    isSnapshot = false
}) => {
    const stores = useStore();
    const isPoolShift = isPoolWorkType(workType);
    // Saf havuzda vardiya bir şubeye değil havuza bağlı olduğu için firma/şube ile daraltma
    // anlamsız; daraltma havuz bölgesi filtresiyle yapılır. Sabit entegrede havuz tek şubeye
    // bağlı olduğundan firma/şube filtreleri korunur.
    const isPurePool = workType === SHIFT_WORK_TYPES.POOL;
    const isCompanyUser = stores.meStore.checkRole("isCompanyUser");
    // Panel kullanıcıları firma/alt firma filtresini her zaman görür; firma kullanıcısında
    // tek firmaya bağlıysa seçim anlamsız olduğu için gizlenir.
    const canSelectCompany =
        !isPurePool &&
        (!isCompanyUser ||
            stores.meStore.checkRole("userPermission") ||
            stores.meStore.auth?.companies?.length > 1);
    const canSelectSubCompany =
        canSelectCompany &&
        (!isCompanyUser ||
            stores.meStore.auth?.subCompany === undefined ||
            stores.meStore.auth?.subcompanies?.length > 1);

    const [city, setCity] = useState(filters.city);
    const [company, setCompany] = useState(filters.company);
    const [subcompany, setSubcompany] = useState(filters.subcompany || []);
    const [pool, setPool] = useState(filters.pool || []);
    const [status, setStatus] = useState(filters.status || "all");
    // Sabit vardiyada artık yalnız tarih aralığı seçiliyor; `period` backend'e uyumluluk için
    // filtre nesnesinde korunuyor ama başlangıç/bitiş tarihi gönderildiğinde etkisiz.
    const [period] = useState(filters.period);
    const [startDate, setStartDate] = useState(filters.startDate);
    const [endDate, setEndDate] = useState(filters.endDate);
    const [date, setDate] = useState(moment(filters.date || undefined));
    const [searchText, setSearchText] = useState(filters.searchText);

    // Operasyon Yönetimi filtre barındaki (LiveOperationFilters) firma listesi kuralı: şehir
    // seçiliyken yalnız o şehirde şubesi olan firmalar listelenir.
    const [, subCompaniesInCity] = useFetch({
        defaultDataValue: [],
        fetchFn: stores.companyStore.getSubCompanyMapList.bind(null, {
            city,
            disabled: "false"
        }),
        dependencies: [city],
        condition: Boolean(city) && city !== "all"
    });

    const companyIdsWithBranchInCity = useMemo(
        () => new Set((subCompaniesInCity || []).map(subCompany => subCompany.companyId)),
        [subCompaniesInCity]
    );

    const companyCityFilter = (companyOption, selectedCity) => {
        if (!selectedCity || selectedCity === "all") return true;
        return companyIdsWithBranchInCity.has(companyOption?._id);
    };

    useEffect(() => {
        if (city !== "") {
            setCompany(undefined);
            setSubcompany([]);
            setPool([]);
        }
    }, [city]);

    useEffect(() => {
        setSubcompany([]);
    }, [company]);

    // Saf havuza geçildiğinde firma/şube filtreleri gizlendiği için seçili kalan bir değer
    // listeyi sessizce daraltmasın diye sıfırlanır.
    useEffect(() => {
        if (isPurePool) {
            setCompany(undefined);
            setSubcompany([]);
        }
    }, [isPurePool]);

    // Alt firma tipi değişince, yeni tipte gizlenen bir durum seçili kaldıysa sıfırla; aksi
    // halde filtre gizli bir seçenekle boş liste üretir. Havuz / sabit entegrede puantaj
    // durumları, sabitte ise tamamlanma durumları gizli.
    useEffect(() => {
        const hiddenStatuses = isPoolShift
            ? REPORT_ONLY_SHIFT_STATUSES
            : DEDICATED_HIDDEN_SHIFT_STATUSES;
        if (hiddenStatuses.includes(status)) {
            setStatus("all");
        }
    }, [isPoolShift, status]);

    useEffect(() => {
        setCurrentPage(1);
    }, [city, company, subcompany, pool, status, period, startDate, endDate, date, searchText]);

    useEffect(() => {
        const newFilters = {
            city,
            company,
            subcompany,
            pool,
            status,
            period,
            startDate,
            endDate,
            date,
            searchText,
            courier: filters.courier
        };
        if (JSON.stringify(filters) !== JSON.stringify(newFilters)) {
            setFilters(newFilters);
        }
    }, [
        city,
        company,
        subcompany,
        pool,
        status,
        period,
        startDate,
        endDate,
        date,
        searchText,
        workType
    ]);

    return (
        <React.Fragment>
            {!isSnapshot && !isCompanyUser && (
                <Col xs={20} sm={10} md={6} lg={4} xl={3}>
                    <SubCompanyWorkTypeFilter
                        showAllOption={false}
                        showDedicatedIntegrationOption
                        // "dedicatedIntegration" değeri meStore.map.filters.workType'ı paylaşan
                        // diğer ekranlarda (ör. Alt Firma Fiyatlandırma) geçersiz olduğu için
                        // bu seçim sayfada yerel tutuluyor.
                        syncWithStore={false}
                        value={workType}
                        onChange={value => {
                            setWorkType(value);
                            setCurrentPage(1);
                        }}
                    />
                </Col>
            )}
            <Col xs={20} sm={10} md={6} lg={4} xl={3}>
                <CityFilter value={city} onChange={setCity} />
            </Col>
            {canSelectCompany && (
                <Col xs={20} sm={10} md={6} lg={4} xl={3}>
                    <CompanyFilter
                        value={company}
                        onChange={setCompany}
                        showAllOption
                        allKey=""
                        placeholder="Tüm Firmalar"
                        city={city}
                        cityFilter={companyCityFilter}
                    />
                </Col>
            )}

            {canSelectSubCompany && (
                <Col xs={20} sm={10} md={6} lg={4} xl={3}>
                    <h5>Alt Firma</h5>
                    <SubCompanyFilter
                        value={subcompany}
                        onChange={setSubcompany}
                        company={company}
                        city={city}
                        mode="multiple"
                        showAllOption={false}
                        allowClear
                        showSearch
                        disabled={false}
                        placeholder="Tüm Alt Firmalar"
                        hideTitle
                    />{" "}
                </Col>
            )}

            {!isSnapshot && !isCompanyUser && workType === SHIFT_WORK_TYPES.POOL && (
                <Col xs={20} sm={10} md={6} lg={4} xl={3}>
                    {/* Filtre yalnız havuz tipinde gösterilir: sabitte havuz kavramı yok, sabit
                        entegrede ise havuz tek şubeye bağlı olduğu için daraltma firma/şube
                        filtresiyle yapılıyor. Liste Operasyon Yönetimi (LiveOperationFilters) ile
                        birebir aynı kuralla geliyor: tek alt firmalı havuzlar dedike entegrasyon
                        sayılıp elenir. */}
                    <PoolFilter
                        value={pool}
                        onChange={setPool}
                        city={city}
                        mode="multiple"
                        disabled={false}
                        showAllOption={false}
                        placeholder="Tüm Havuzlar"
                        optionFilter={poolOption => (poolOption.subCompanies?.length || 0) !== 1}
                    />
                </Col>
            )}

            {!isSnapshot && (
                <Col xs={20} sm={10} md={6} lg={4} xl={3}>
                    <PoolShiftStatusFilter
                        value={status}
                        onChange={setStatus}
                        excludeReportStatuses={isPoolShift}
                        excludeDedicatedHiddenStatuses={!isPoolShift}
                    />
                </Col>
            )}

            <Col xs={20} sm={10} md={6} lg={4} xl={3}>
                <SearchText value={searchText} onChange={setSearchText} />
            </Col>
            <Col xs={20} sm={10} md={8} lg={6} xl={5}>
                {isPoolShift ? (
                    <div style={{marginBottom: 20}}>
                        <DateFilter value={date} onChange={setDate} />
                    </div>
                ) : (
                    <DateRangeFilter
                        showLabel
                        allowClear={false}
                        placeholder={["Başlangıç Tarihi", "Bitiş Tarihi"]}
                        value={startDate && endDate ? [moment(startDate), moment(endDate)] : null}
                        ranges={{
                            Dün: [moment().subtract(1, "day"), moment().subtract(1, "day")],
                            Bugün: [moment(), moment()],
                            Yarın: [moment().add(1, "day"), moment().add(1, "day")]
                        }}
                        onChange={dates => {
                            setStartDate(dates?.[0] || undefined);
                            setEndDate(dates?.[1] || undefined);
                        }}
                    />
                )}
            </Col>
        </React.Fragment>
    );
};

export default ShiftManagementFilters;
